import { canonicalTreeKey, cloneCatalog, resourceReachable } from './catalog';
import { childIdentity, resourceChildren, storeChildOrder } from './order';

export function siblingKeys(catalog, parent = '') {
    return Object.entries(catalog.resources).filter(([, node]) => (node.parent || '') === parent)
        .sort(([a, x], [b, y]) => (x.sort || 0) - (y.sort || 0) || a.localeCompare(b)).map(([key]) => key);
}
export function movableTreeNode(catalog, node) {
    if (!node?.resource || !catalog.resources[node.resource]) return false;
    return node.key === (node.referenceOwner
        ? `${canonicalTreeKey(catalog, node.referenceOwner)}/@${node.resource}`
        : canonicalTreeKey(catalog, node.resource));
}
function inheritedGroup(catalog, key) {
    const seen = new Set();
    while (key && !seen.has(key)) {
        seen.add(key);
        const node = catalog.resources[key];
        if (!node) break;
        if (node.group) return node.group;
        key = node.parent;
    }
    return '';
}
function insertAt(order, item, before, after) {
    if (before && after) throw new Error('移动位置无效');
    const identity = childIdentity(item);
    const requested = before || after;
    const anchor = typeof requested === 'string' ? { kind: item.kind, key: requested } : requested;
    if (anchor && childIdentity(anchor) === identity) {
        if (!order.some(entry => childIdentity(entry) === identity)) throw new Error('目标位置无效');
        return order;
    }
    const remaining = order.filter(entry => childIdentity(entry) !== identity);
    const index = anchor ? remaining.findIndex(entry => childIdentity(entry) === childIdentity(anchor)) : remaining.length;
    if (index < 0) throw new Error('目标位置已变化，请重新选择');
    remaining.splice(index + (after ? 1 : 0), 0, item);
    return remaining;
}
const sameOrder = (a, b) => a.map(childIdentity).join('|') === b.map(childIdentity).join('|');
export function canMoveResource(catalog, key, parent = '') {
    return Boolean(catalog.resources[key]) && (!parent || catalog.resources[parent]?.kind === 'directory')
        && (!parent || !resourceReachable(catalog, key, parent));
}
export function moveResource(catalog, key, { parent = '', before, after }) {
    if (!canMoveResource(catalog, key, parent)) throw new Error('不能移入自身、子节点或会形成引用循环的目录');
    const original = catalog.resources[key];
    const previousOrder = resourceChildren(catalog, parent);
    const order = insertAt(previousOrder, { kind: 'node', key }, before, after)
        .filter(item => !(item.kind === 'reference' && item.key === key));
    if ((original.parent || '') === parent && sameOrder(order, previousOrder)) return catalog;
    const next = cloneCatalog(catalog);
    next.resources[key].parent = parent;
    if ((original.parent || '') !== parent && !original.group) {
        const group = inheritedGroup(catalog, key);
        if (group) next.resources[key].group = group;
    }
    if (parent && next.resources[parent].references?.includes(key)) {
        next.resources[parent].references = next.resources[parent].references.filter(item => item !== key);
        if (next.resources[parent].reference_operations) delete next.resources[parent].reference_operations[key];
    }
    if ((original.parent || '') !== parent && next.resources[original.parent]?.child_order) {
        storeChildOrder(next, original.parent, resourceChildren(next, original.parent));
    }
    storeChildOrder(next, parent, order);
    return next;
}
export function moveReference(catalog, owner, key, { parent = owner, before, after }) {
    if (parent !== owner || !catalog.resources[owner]?.references?.includes(key)) throw new Error('引用只能在所属节点内调整顺序');
    const previous = resourceChildren(catalog, owner);
    const order = insertAt(previous, { kind: 'reference', key }, before, after);
    if (sameOrder(order, previous)) return catalog;
    const next = cloneCatalog(catalog);
    storeChildOrder(next, owner, order);
    return next;
}
export function stepDestination(catalog, node, direction) {
    if (!movableTreeNode(catalog, node)) return null;
    const parent = node.referenceOwner || catalog.resources[node.resource].parent || '';
    const order = resourceChildren(catalog, parent);
    const identity = childIdentity({ kind: node.referenceOwner ? 'reference' : 'node', key: node.resource });
    const neighbor = order[order.findIndex(item => childIdentity(item) === identity) + direction];
    return neighbor ? { parent, [direction < 0 ? 'before' : 'after']: neighbor } : null;
}
export function dropDestination(catalog, source, target, position) {
    if (!movableTreeNode(catalog, source) || !movableTreeNode(catalog, target) || source.key === target.key) throw new Error('请选择可移动的真实节点或引用');
    const targetParent = target.referenceOwner || catalog.resources[target.resource].parent || '';
    const anchor = { kind: target.referenceOwner ? 'reference' : 'node', key: target.resource };
    if (source.referenceOwner) {
        if (!position && !target.referenceOwner && target.resource === source.referenceOwner) return { parent: source.referenceOwner };
        if (!position || source.referenceOwner !== targetParent) throw new Error('引用只能在所属节点内调整顺序');
        return { parent: source.referenceOwner, [position < 0 ? 'before' : 'after']: anchor };
    }
    if (!position && target.referenceOwner) throw new Error('请放到引用前后，或放入真实目录');
    const parent = position ? targetParent : target.resource;
    if (!canMoveResource(catalog, source.resource, parent)) throw new Error('目标必须是目录，且不能形成目录或引用循环');
    return { parent, ...(position ? { [position < 0 ? 'before' : 'after']: anchor } : {}) };
}
export function moveTreeNode(catalog, source, destination) {
    return source.referenceOwner
        ? moveReference(catalog, source.referenceOwner, source.resource, destination)
        : moveResource(catalog, source.resource, destination);
}

// Resolve against the row under the pointer, rather than the tree library's
// previous flattened row and horizontal indentation heuristics.
export function pointerDestination(catalog, source, target, ratio) {
    if (!source || !target || source.key === target.key || !Number.isFinite(ratio)) return null;
    const owner = source.referenceOwner;
    const sourceParent = owner || catalog.resources[source.resource]?.parent || '';
    const targetParent = target.referenceOwner || catalog.resources[target.resource]?.parent || '';
    const middle = ratio > 0.25 && ratio < 0.75;
    const inside = !target.referenceOwner && (!owner || target.resource === owner)
        && catalog.resources[target.resource]?.kind === 'directory' && middle;
    let position = inside ? 0 : ratio < 0.5 ? -1 : 1;
    if (!inside && sourceParent === targetParent) {
        const order = resourceChildren(catalog, sourceParent);
        const from = order.findIndex(item => item.kind === (owner ? 'reference' : 'node') && item.key === source.resource);
        const to = order.findIndex(item => item.kind === (target.referenceOwner ? 'reference' : 'node') && item.key === target.resource);
        if (from < 0 || to < 0) return null;
        // Dropping on the old gap beside an adjacent sibling should exchange
        // positions instead of silently leaving the node where it started.
        if (middle || (position < 0 && from === to - 1) || (position > 0 && from === to + 1)) position = from > to ? -1 : 1;
    }
    try {
        return { key: target.key, mode: position === 0 ? 'inside' : position < 0 ? 'before' : 'after', destination: dropDestination(catalog, source, target, position) };
    } catch { return null; }
}
